import React, { useState, useEffect } from 'react';
import { Breadcrumb, Card, Row, Col, Tabs, Select, Button, Input, Progress, Icon } from 'antd';
import { Chart, Geom, Axis, Tooltip, Legend } from 'bizcharts';
import DataSet from '@antv/data-set';
import { Link } from 'react-router-dom';
import { buildQualityDashboardUri, buildQualityDashboardDemo2Uri } from 'utils/routes';
import { StyledCard, SectionTitle, TagSummaryCard } from './QualityDashboard.styles';

const { Option } = Select;

// Review Tag Summary: category cards with tags and percentages (same as main Quality Dashboard)
const reviewTagSummary = [
  {
    key: 'accuracy',
    name: 'Accuracy',
    icon: 'file-text',
    reviewCount: 108,
    tags: [
      { label: 'Looked like the photos', percent: 21 },
      { label: 'Matched the description', percent: 20 },
      { label: 'Had listed amenities & services', percent: 16 }
    ]
  },
  {
    key: 'cleanliness',
    name: 'Cleanliness',
    icon: 'home',
    reviewCount: 108,
    tags: [
      { label: 'Spotless furniture & linens', percent: 21 },
      { label: 'Pristine kitchen', percent: 20 },
      { label: 'Squeaky-clean bathroom', percent: 20 },
      { label: 'Free of clutter', percent: 17 }
    ]
  },
  {
    key: 'checkin',
    name: 'Check-in',
    icon: 'login',
    reviewCount: 108,
    tags: [
      { label: 'Clear instructions', percent: 22 },
      { label: 'Easy to get inside', percent: 17 },
      { label: 'Felt right at home', percent: 15 },
      { label: 'Responsive Host', percent: 15 },
      { label: 'Easy to find', percent: 14 }
    ]
  },
  {
    key: 'communication',
    name: 'Communication',
    icon: 'message',
    reviewCount: 108,
    tags: [
      { label: 'Considerate', percent: 21 },
      { label: 'Always responsive', percent: 18 },
      { label: 'Helpful instructions', percent: 17 },
      { label: 'Proactive', percent: 6 }
    ]
  },
  {
    key: 'location',
    name: 'Location',
    icon: 'environment',
    reviewCount: 108,
    tags: [
      { label: 'Peaceful', percent: 21 },
      { label: 'Beautiful surroundings', percent: 20 },
      { label: 'Private', percent: 13 },
      { label: 'Walkable', percent: 4 }
    ]
  }
];

// Dummy: quality metrics over time (monthly) for combo chart
const metricsOverTimeData = [
  { month: 'Apr 2025', rating: 4.6, fiveStarRate: 0.72 },
  { month: 'May 2025', rating: 4.65, fiveStarRate: 0.78 },
  { month: 'Jun 2025', rating: 4.7, fiveStarRate: 0.82 },
  { month: 'Jul 2025', rating: 4.72, fiveStarRate: 0.85 },
  { month: 'Aug 2025', rating: 4.75, fiveStarRate: 0.88 },
  { month: 'Sep 2025', rating: 4.78, fiveStarRate: 0.92 },
  { month: 'Oct 2025', rating: 4.8, fiveStarRate: 0.95 },
  { month: 'Nov 2025', rating: 4.82, fiveStarRate: 0.97 },
  { month: 'Dec 2025', rating: 4.85, fiveStarRate: 0.98 },
  { month: 'Jan 2026', rating: 4.88, fiveStarRate: 1.0 },
  { month: 'Feb 2026', rating: 4.8, fiveStarRate: 1.0 }
];

// Per-listing review stats (category ratings 1–5)
const perListingStats = [
  {
    name: 'Canyon Views & Central Location',
    overall: 5.0,
    reviewCount: 21,
    categories: { checkin: 5, accuracy: 5, location: 5, communication: 5, value: 5, cleanliness: 5 }
  },
  {
    name: 'Seaside View Apartment',
    overall: 4.8,
    reviewCount: 34,
    categories: { checkin: 5, accuracy: 4.8, location: 5, communication: 4.9, value: 4.7, cleanliness: 4.8 }
  },
  {
    name: 'City Central Studio',
    overall: 4.6,
    reviewCount: 18,
    categories: { checkin: 4.5, accuracy: 4.7, location: 4.6, communication: 4.6, value: 4.5, cleanliness: 4.7 }
  }
];

// Opportunities dummy
const opportunitiesList = [
  {
    id: '1',
    title: 'Offer weekly discounts',
    subtitle: 'Weekly Discount',
    description: 'Attract guests looking to book for 7 nights or more.',
    detail: 'Available for 1 listing. 0% of your listings already applied.',
    inputLabel: 'Weekly discount (1-99) %',
    inputPlaceholder: 'Min: 1'
  },
  {
    id: '2',
    title: 'Offer monthly discounts',
    subtitle: 'Monthly Discount',
    description: "Encourage guests to stay longer by offering a monthly discount—you'll spend less time turning over your space between guests.",
    detail: 'Available for 1 listing. 0% of your listings already applied.',
    inputLabel: 'Monthly discount (1-99) %',
    inputPlaceholder: 'Min: 1'
  },
  {
    id: '3',
    title: 'Enable instant book',
    subtitle: 'Instant Book',
    description: 'Let guests book without waiting for approval.',
    detail: 'Available for 3 listings. 33% already applied.',
    inputLabel: null,
    inputPlaceholder: null
  }
];

// Reviews list dummy
const reviewsList = [
  {
    id: '1',
    stars: 5,
    listing: 'Canyon Views & Central Location',
    text: 'We were in the area for a family reunion so our primary goal was staying at a place to refresh and relax. This was the perfect spot!',
    date: 'Feb 24, 2026',
    cleanliness: 5,
    accuracy: 5,
    checkin: 5,
    communication: 5,
    location: 5,
    value: 5
  },
  {
    id: '2',
    stars: 5,
    listing: 'Canyon Views & Central Location',
    text: 'Great location, very clean, and the host was super responsive. Would definitely book again.',
    date: 'Feb 18, 2026',
    cleanliness: 5,
    accuracy: 5,
    checkin: 5,
    communication: 5,
    location: 5,
    value: 5
  },
  {
    id: '3',
    stars: 5,
    listing: 'Canyon Views & Central Location',
    text: 'Perfect for our weekend getaway. The place had everything we needed and the view was amazing.',
    date: 'Feb 9, 2026',
    cleanliness: 5,
    accuracy: 5,
    checkin: 5,
    communication: 5,
    location: 5,
    value: 5
  },
  {
    id: '4',
    stars: 4,
    listing: 'Seaside View Apartment',
    text: 'Lovely apartment by the sea. Check-in was smooth. Only minor issue was the WiFi was slow in the evening.',
    date: 'Feb 2, 2026',
    cleanliness: 5,
    accuracy: 4,
    checkin: 5,
    communication: 5,
    location: 5,
    value: 4
  }
];

const CATEGORY_LABELS = [
  { key: 'checkin', label: 'Check-in' },
  { key: 'accuracy', label: 'Accuracy' },
  { key: 'location', label: 'Location' },
  { key: 'communication', label: 'Communication' },
  { key: 'value', label: 'Value' },
  { key: 'cleanliness', label: 'Cleanliness' }
];

const QualityDashboardDemo2 = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [listingFilter, setListingFilter] = useState('All Listings');
  const [periodFilter, setPeriodFilter] = useState('Last 12 Months');

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [activeTab]);

  const ds = new DataSet();
  const dv = ds.createView().source(metricsOverTimeData);
  dv.transform({
    type: 'fold',
    fields: ['rating', 'fiveStarRate'],
    key: 'metric',
    value: 'value'
  }).transform({
    type: 'map',
    callback: row => ({
      ...row,
      metric: row.metric === 'rating' ? 'Average Rating' : '5-Star Rate',
      value: row.metric === 'fiveStarRate' ? row.value * 100 : row.value
    })
  });

  return (
    <StyledCard
      title={
        <span>
          Airbnb Quality Dashboard (Demo 2 — Host Tools){' '}
          <a
            href="https://youtu.be/a0xJ5ZuEnpU?t=16"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 400, fontSize: 12, marginLeft: 8 }}
          >
            Reference Link - Host Tools
          </a>
        </span>
      }
      extra={
        <div style={{ display: 'flex', gap: 12 }}>
          <Select value={listingFilter} onChange={setListingFilter} style={{ minWidth: 160 }}>
            <Option value="All Listings">All Listings</Option>
            <Option value="Canyon Views">Canyon Views & Central Location</Option>
            <Option value="Seaside">Seaside View Apartment</Option>
            <Option value="City Central">City Central Studio</Option>
          </Select>
          <Select value={periodFilter} onChange={setPeriodFilter} style={{ minWidth: 160 }}>
            <Option value="Last 12 Months">Last 12 Months</Option>
            <Option value="Last 6 Months">Last 6 Months</Option>
            <Option value="Last 30 Days">Last 30 Days</Option>
          </Select>
        </div>
      }
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" style={{ borderRadius: 8, border: '1px solid #e8e8e8' }}>
            <div style={{ fontSize: 28, fontWeight: 600, color: '#262626' }}>4.8</div>
            <div style={{ fontSize: 13, color: '#8c8c8c' }}>out of 5.0</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" style={{ borderRadius: 8, border: '1px solid #e8e8e8' }}>
            <div style={{ fontSize: 28, fontWeight: 600, color: '#262626' }}>100%</div>
            <div style={{ fontSize: 13, color: '#8c8c8c' }}>of all reviews</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" style={{ borderRadius: 8, border: '1px solid #e8e8e8' }}>
            <div style={{ fontSize: 28, fontWeight: 600, color: '#262626' }}>1,746</div>
            <div style={{ fontSize: 13, color: '#8c8c8c' }}>across all listings</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" style={{ borderRadius: 8, border: '1px solid #e8e8e8' }}>
            <div style={{ fontSize: 28, fontWeight: 600, color: '#262626' }}>69</div>
            <div style={{ fontSize: 13, color: '#8c8c8c' }}>in selected period</div>
          </Card>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="Overview" key="overview">
          <Card title="Quality Metrics Over Time" size="small" style={{ marginBottom: 24, borderRadius: 8 }}>
            <Chart height={320} data={dv} forceFit>
              <Legend />
              <Axis name="month" />
              <Axis name="value" title={{ text: 'Rating / %' }} />
              <Tooltip formatter={(value, name) => [name === '5-Star Rate' ? `${value}%` : value, name]} />
              <Geom type="area" position="month*value" color="metric" opacity={0.2} shape="smooth" />
              <Geom type="line" position="month*value" color="metric" size={2} shape="smooth" />
            </Chart>
          </Card>
          <Card title="Per-Listing Review Stats" size="small" style={{ borderRadius: 8 }}>
            {perListingStats.map((listing, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: idx < perListingStats.length - 1 ? 24 : 0,
                  paddingBottom: idx < perListingStats.length - 1 ? 24 : 0,
                  borderBottom: idx < perListingStats.length - 1 ? '1px solid #f0f0f0' : 'none'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{listing.name}</span>
                  <span style={{ fontSize: 13, color: '#666' }}>
                    {listing.overall} ({listing.reviewCount} reviews)
                  </span>
                </div>
                <Row gutter={[12, 12]}>
                  {CATEGORY_LABELS.map(({ key, label }) => (
                    <Col xs={24} sm={12} key={key}>
                      <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>{label}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Progress
                          percent={(listing.categories[key] / 5) * 100}
                          showInfo={false}
                          strokeColor="#1890ff"
                          style={{ flex: 1, marginBottom: 0 }}
                        />
                        <span style={{ fontSize: 13, minWidth: 36 }}>{listing.categories[key]}/5</span>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            ))}
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Listing Issues" key="issues">
          <Card size="small">
            <p style={{ color: '#666' }}>No listing issues in the selected period. (Dummy content for demo.)</p>
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Reviews" key="reviews">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {reviewsList.map(review => (
              <Card key={review.id} size="small" style={{ borderRadius: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      {[...Array(5)].map((_, i) => (
                        <Icon key={i} type="star" theme="filled" style={{ color: i < review.stars ? '#fadb14' : '#d9d9d9', marginRight: 2 }} />
                      ))}{' '}
                      <span style={{ fontSize: 13 }}>{review.stars}/5</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>{review.listing}</div>
                    <p style={{ margin: '0 0 8px', fontSize: 14 }}>{review.text}</p>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                      Cleanliness: {review.cleanliness}/5 Accuracy: {review.accuracy}/5 Check-in: {review.checkin}/5 Communication:{' '}
                      {review.communication}/5 Location: {review.location}/5 Value: {review.value}/5
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: '#8c8c8c', whiteSpace: 'nowrap' }}>{review.date}</div>
                </div>
              </Card>
            ))}
          </div>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Review Tags" key="tags">
          <SectionTitle>Review Tag Summary</SectionTitle>
          <p style={{ fontSize: 12, color: '#666', marginBottom: 16 }}>
            When guests leave reviews, they rate Accuracy, Cleanliness, Check-in, Communication, and Location. In each card you see the number of
            reviews for that category and the most frequent tags with their share.
          </p>
          <Row gutter={[16, 16]}>
            {reviewTagSummary.map(cat => (
              <Col xs={24} sm={12} md={8} key={cat.key}>
                <TagSummaryCard size="small">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>
                      <Icon type={cat.icon} style={{ marginRight: 8 }} />
                      {cat.name}
                    </span>
                    <span style={{ fontSize: 12, color: '#666' }}>reviews: {cat.reviewCount}</span>
                  </div>
                  <div>
                    {cat.tags.map((tag, idx) => (
                      <div key={idx} style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                          <span>{tag.label}</span>
                          <span>{tag.percent}%</span>
                        </div>
                        <div style={{ height: 6, borderRadius: 3, background: '#e8e8e8', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${tag.percent}%`, background: '#b7eb8f', borderRadius: 3 }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </TagSummaryCard>
              </Col>
            ))}
          </Row>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Checkout Tasks" key="checkout">
          <Card size="small">
            <p style={{ color: '#666' }}>Checkout tasks. (Dummy content for demo.)</p>
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Opportunities" key="opportunities">
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} md={8}>
              <Card size="small" style={{ textAlign: 'center', borderRadius: 8 }}>
                <div style={{ fontSize: 32, fontWeight: 600, color: '#1890ff' }}>69%</div>
                <div style={{ fontSize: 13, color: '#8c8c8c' }}>Your Completion</div>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small" style={{ textAlign: 'center', borderRadius: 8 }}>
                <div style={{ fontSize: 32, fontWeight: 600, color: '#262626' }}>47%</div>
                <div style={{ fontSize: 13, color: '#8c8c8c' }}>Airbnb Host Average</div>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small" style={{ textAlign: 'center', borderRadius: 8 }}>
                <div style={{ fontSize: 32, fontWeight: 600, color: '#262626' }}>53%</div>
                <div style={{ fontSize: 13, color: '#8c8c8c' }}>Similar Hosts Average</div>
              </Card>
            </Col>
          </Row>
          <Card title={`Available Opportunities (${opportunitiesList.length})`} size="small" style={{ borderRadius: 8 }}>
            {opportunitiesList.map(opp => (
              <div key={opp.id} style={{ padding: '16px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
                  {opp.title} <strong>{opp.subtitle}</strong>
                </div>
                <p style={{ margin: '0 0 8px', fontSize: 13, color: '#595959' }}>{opp.description}</p>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>{opp.detail}</div>
                {opp.inputLabel && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, marginBottom: 4 }}>{opp.inputLabel}</div>
                    <Input placeholder={opp.inputPlaceholder} style={{ maxWidth: 200 }} />
                  </div>
                )}
                <Button type="primary">Apply</Button>
              </div>
            ))}
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Notifications" key="notifications">
          <Card size="small">
            <p style={{ color: '#666' }}>No new notifications. (Dummy content for demo.)</p>
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Ground Rules" key="ground">
          <Card size="small">
            <p style={{ color: '#666' }}>Ground rules. (Dummy content for demo.)</p>
          </Card>
        </Tabs.TabPane>
      </Tabs>

      <div style={{ marginTop: 32, fontSize: 12, color: '#8c8c8c', textAlign: 'right' }}>
        <Link to={buildQualityDashboardUri()}>← Back to Quality Dashboard (current version)</Link>
      </div>
    </StyledCard>
  );
};

export default QualityDashboardDemo2;
